import { Link } from "react-router-dom";
import { ArrowLeft, HelpCircle, Smartphone, Link2, CreditCard, Zap, Mail, CheckCircle2, Store, QrCode } from "lucide-react";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

/**
 * Centro Assistenza — public page.
 * Linked from the Footer (always) and from Dashboard (when authenticated).
 * Includes FAQ + step-by-step guide to configure a RecenUp Card.
 */
export default function Help() {
  const { user } = useAuth();
  const backHref = user ? "/dashboard" : "/";
  const backLabel = user ? "Dashboard" : "Home";

  return (
    <div className="min-h-screen bg-[var(--bg-1)] text-[var(--fg-1)]" data-testid="help-page">
      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to={backHref} data-testid="help-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]" style={{ transition: "color 0.2s ease" }}>
            <ArrowLeft size={14} /> {backLabel}
          </Link>
          <div className="font-serif text-2xl text-[var(--fg-1)] tracking-tight leading-none">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-sans font-semibold text-[#00F2F4]">
            <HelpCircle size={12} /> Centro Assistenza
          </div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Come possiamo aiutarti?</h1>
          <p className="mt-4 text-sm md:text-base text-[var(--fg-3)] max-w-2xl leading-relaxed">
            Trova subito risposte alle domande più frequenti e segui la guida passo passo per configurare la tua RecenUp Card.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contatti" data-testid="help-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans font-semibold" style={{ transition: "background-color 0.2s ease" }}>
              <Mail size={14} /> Contattaci
            </Link>
            <a href="#guida-card" data-testid="help-guide-anchor" className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans" style={{ transition: "border-color 0.2s ease" }}>
              <Zap size={14} /> Guida alla card
            </a>
          </div>
        </div>

        <section id="guida-card" data-testid="help-section-guide" className="mt-4 rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8">
          <h2 className="font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight">Come configurare la tua RecenUp Card</h2>
          <p className="mt-2 text-sm text-[var(--fg-3)]">Segui questi passaggi dopo aver ricevuto la card.</p>

          <ol className="mt-6 space-y-5">
            <Step n={1} icon={CheckCircle2} title="Accedi al tuo account">
              Vai su <strong>recenup.com</strong> e accedi con l'email usata per l'acquisto. Se non hai ancora un account, registrati gratuitamente.
            </Step>
            <Step n={2} icon={Link2} title="Collega Google Business Profile">
              Dal dashboard apri <em>«Collega Google»</em> e autorizza RecenUp a leggere le recensioni della tua attività. Non pubblichiamo nulla senza il tuo consenso.
            </Step>
            <Step n={3} icon={Store} title="Seleziona l'attività corretta">
              Se gestisci più sedi, scegli dall'elenco quella alla quale associare la card. Puoi collegare più attività se hai un piano Pro.
            </Step>
            <Step n={4} icon={CreditCard} title="Associa la card all'attività">
              Dalla sezione <em>«Le mie card»</em>, inserisci l'ID stampato sul retro della RecenUp Card e associalo all'attività scelta.
            </Step>
            <Step n={5} icon={Smartphone} title="Prova la card NFC">
              Avvicina uno smartphone (iOS o Android recente, NFC attivo) alla card: si aprirà la pagina di raccolta recensione dedicata alla tua attività.
            </Step>
            <Step n={6} icon={QrCode} title="Usa il QR Code di riserva">
              Se un cliente ha problemi con l'NFC, può sempre inquadrare il QR Code presente sulla card. Il link è lo stesso.
            </Step>
            <Step n={7} icon={CheckCircle2} title="Verifica che tutto funzioni">
              Dal dashboard puoi monitorare quante persone hanno scansionato la card e quante recensioni sono arrivate. I primi scan arrivano appena pubblichi la card al pubblico.
            </Step>
          </ol>

          <div className="mt-8 p-4 rounded-xl bg-[var(--bg-3)]/50 border border-[var(--border-c)]">
            <div className="text-xs uppercase tracking-[0.15em] text-[var(--fg-3)] font-sans font-semibold mb-2">Non funziona?</div>
            <p className="text-sm text-[var(--fg-2)] leading-relaxed">
              Verifica che NFC sia attivo sullo smartphone del cliente, che la card non sia schermata da custodie metalliche e che Google Business Profile sia collegato correttamente dal dashboard. Se il problema persiste, <Link to="/contatti" className="text-[#00F2F4] hover:underline">scrivici</Link>.
            </p>
          </div>
        </section>

        <section data-testid="help-section-faq" className="mt-10 rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8">
          <h2 className="font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight">Domande frequenti</h2>
          <div className="mt-6 space-y-6">
            <Faq q="Serve un abbonamento per usare la RecenUp Card?" testId="faq-no-sub">
              No. L'acquisto della card è un <strong>pagamento una tantum</strong>. Puoi usarla con il piano gratuito Free rispettando i limiti del piano (1 attività, 1 card attiva, 1 location Google). L'abbonamento Pro è separato e sblocca funzionalità avanzate.
            </Faq>
            <Faq q="Cosa include il piano Free?" testId="faq-free-plan">
              Il piano gratuito include 1 attività, 1 card attiva, 1 location Google Business Profile, raccolta recensioni e dashboard di base. Nessuna scadenza, nessun costo nascosto.
            </Faq>
            <Faq q="Cosa include il piano Pro a €20/mese?" testId="faq-pro-plan">
              Il piano Pro include più attività, card illimitate, analytics avanzati, AI Insights, risposta AI alle recensioni (100 utilizzi/mese), report programmati, multi-location GBP e collaboratori. Puoi cancellare in qualsiasi momento.
            </Faq>
            <Faq q="Come funziona la prova di 14 giorni?" testId="faq-trial">
              Puoi provare Pro per 14 giorni senza inserire la carta di credito. Al termine non c'è alcun addebito automatico: scegli tu se continuare con Pro o restare su Free.
            </Faq>
            <Faq q="Cosa succede se compro più card ma rimango su Free?" testId="faq-extra-cards">
              Le card eccedenti restano associate al tuo account e non vengono cancellate. Il piano Free consente l'attivazione di una card per volta. Passando a Pro potrai attivare le altre entro i limiti del piano.
            </Faq>
            <Faq q="Come collego Google Business Profile?" testId="faq-gbp">
              Dal dashboard clicca <em>«Collega Google»</em>, accedi con l'account proprietario dell'attività e autorizza la lettura delle recensioni. Puoi scollegare quando vuoi.
            </Faq>
            <Faq q="RecenUp risponde automaticamente alle recensioni?" testId="faq-ai-auto">
              No. L'AI genera soltanto una <strong>proposta</strong> di risposta, che tu puoi modificare, cambiare di tono o rigenerare. La pubblicazione resta sempre una decisione tua.
            </Faq>
            <Faq q="Come posso cancellare l'abbonamento Pro?" testId="faq-cancel">
              Dal tuo profilo apri <em>«Gestisci abbonamento»</em> e procedi con la cancellazione dal portale Stripe. L'abbonamento resta attivo fino alla fine del periodo già pagato.
            </Faq>
            <Faq q="Posso farmi rimborsare una card non usata?" testId="faq-refund">
              Per rimborsi su acquisti card non utilizzate scrivici dalla <Link to="/contatti" className="text-[#00F2F4] hover:underline">pagina Contatti</Link> entro i termini di legge, allegando l'ID dell'ordine.
            </Faq>
          </div>
        </section>

        <section className="mt-10 text-center">
          <p className="text-sm text-[var(--fg-3)]">Non hai trovato la risposta che cercavi?</p>
          <Link to="/contatti" data-testid="help-contact-cta-bottom" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-6 py-3 text-sm font-sans font-semibold" style={{ transition: "background-color 0.2s ease" }}>
            <Mail size={14} /> Apri una richiesta di assistenza
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <li className="flex gap-4" data-testid={`help-step-${n}`}>
      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#00F2F4]/12 text-[#00F2F4] flex items-center justify-center font-sans font-semibold text-sm">
        {n}
      </div>
      <div className="flex-1 pt-1">
        <div className="flex items-center gap-2 text-[var(--fg-1)] font-sans font-semibold">
          <Icon size={14} className="text-[#00F2F4]" /> {title}
        </div>
        <div className="mt-1.5 text-sm text-[var(--fg-2)] leading-relaxed">
          {children}
        </div>
      </div>
    </li>
  );
}

function Faq({ q, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="font-sans font-semibold text-[var(--fg-1)]">{q}</div>
      <div className="mt-1.5 text-sm text-[var(--fg-2)] leading-relaxed">{children}</div>
    </div>
  );
}
